<template>
  <div class="email">
    <StepHeader
      :title="title"
      :theme="theme"
      :shrink-mobile-header="isPersonalLoan"
    >
      <p
        class="email__header-text"
        :class="textColorClass()"
      >
        Get Started
      </p>
    </StepHeader>

    <StepContent :theme="theme">
      <AppInputWithValidation
        v-for="(field, index) in fields"
        :key="index"
        :label="field.label"
        :name="field.name"
        :value="field.value"
        :rules="field.rules"
        :type="field.type"
        :hide-error="!submitted"
        :theme="theme"
        :error="emailError"
        :enterkeyhint="enterkeyhint"
        @input="$_input_updateState(field.vuexAction, $event)"
      />

      <div class="m-auto max-w-500 lg:max-w-400 pt-20 flex">
        <div>
          <AppCheckbox
            :theme="theme"
            :value="consent"
            @input="handleCheckbox($event)"
          >
            <div class="checkbox-text">
              Receive promotions and updates from Spring Financial. It’s free and you can opt-out at any time.
              <br>
            </div>
          </AppCheckbox>

          <div class="ml-50 mt-15 checkbox-text">
            By clicking “continue” I agree to the
            <a
              :class="textColorClass()"
              :href="addTrackingParams(generalTermsOfServiceUrl)"
              target="_blank"
            >terms and conditions</a>
            and
            <a
              :class="textColorClass()"
              :href="addTrackingParams(privacyPolicyUrl)"
              target="_blank"
            >privacy policy</a>,
            and for Spring Financial to use my credit information provided by a Credit Rating Agency to qualify me for potential services. I am aware that this will not impact my credit score.
          </div>
        </div>
      </div>
    </StepContent>

    <StepFooter hide-mobile>
      <AppButton
        :theme="theme"
        @click="handleContinue"
      >
        Continue
      </AppButton>
    </StepFooter>
  </div>
</template>

<script>
import AppButton from '@/components/common/buttons/AppButton'
import AppInputWithValidation from '@/components/common/inputs/AppInputWithValidation'
import AppCheckbox from '@/components/common/inputs/AppCheckbox'
import StepContent from '@/components/common/step-layout/StepContent'
import StepFooter from '@/components/common/step-layout/StepFooter'
import StepHeader from '@/components/common/step-layout/StepHeader'

import input from '@/mixins/input'
import navigation from '@/mixins/navigation'
import submitLoans from '@/mixins/submit/submit-loans'
import theme from '@/mixins/theme'
import tracking from '@/mixins/tracking'
import DataLayer from '@/utilities/data-layer'
import thirdPartyLogin from '@/mixins/third-party-login'

export default {
  name: 'Email',

  components: {
    AppButton,
    AppInputWithValidation,
    StepContent,
    StepFooter,
    StepHeader,
    AppCheckbox
  },

  mixins: [
    input,
    navigation,
    submitLoans,
    theme,
    tracking,
    thirdPartyLogin
  ],

  props: {
    title: {
      type: String,
      required: true
    },

    enableInProgress: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      consent: false,
      enterkeyhint: 'go'
    }
  },

  computed: {
    isPrepop () {
      return this.$store.state.application.isPrepop
    },
    firstName () {
      return this.$store.state.customer.first_name
    },

    lastName () {
      return this.$store.state.customer.last_name
    },

    email () {
      return this.$store.state.customer.email
    },

    fbc () {
      return this.$store.state.tracking.fbc
    },

    fbp () {
      return this.$store.state.tracking.fbp
    },

    generalTermsOfServiceUrl () {
      return `${process.env.VUE_APP_MINISITE_URL}general-terms-of-service`
    },

    privacyPolicyUrl () {
      return `${process.env.VUE_APP_MINISITE_URL}privacy-policy`
    },

    fields () {
      return [{
        label: 'Email Address',
        name: 'email',
        type: 'email',
        value: this.email,
        rules: 'required:Email|email',
        vuexAction: 'setEmail'
      }]
    },

    dataLayerObject () {
      return {
        event: `spring_${this.eventPrefix}_email_continue`
      }
    },

    isPersonalLoan () {
      return this.$store.getters['loans/isPersonalLoan']
    },

    isConsentCEM () {
      return this.$store.state.tracking.consent_CEMs
    },

    emailError () {
      return this.$store.state.validations.emailError
    }
  },

  watch: {
    $_navigation_triggerContinue: async function (val) {
      if (val) await this.handleContinue()
      this.$store.dispatch('setTriggerContinue', false)
    },

    isConsentCEM (val) {
      this.consent = val
    }
  },

  created () {
    const { email } = this.$route.query
    if (email) this.$_input_updateState('setEmail', email)
  },

  mounted () {
    if (this.isPrepop) {
      DataLayer.pushEvent('event', `spring_ploan_prepop_insufficient_app_landed`)
      window.rudderanalytics.track('spring_ploan_prepop_insufficient_app_landed')
    }
  },

  methods: {
    handleCheckbox (consent) {
      this.consent = +consent
      this.$store.dispatch('setConsentCEM', +this.consent)
      DataLayer.pushEvent('event', `spring_${this.eventPrefix}_promo_checkbox_clicked`)
    },

    async handleContinue () {
      this.submitted = true
      const isValid = await this.$_navigation_validateStep()
      if (!isValid) return
      if (this.eventPrefix === 'ploan') {
        window.rudderanalytics.identify(this.email, {
          fbc: this.fbc || null,
          fbp: this.fbp || null,
          email: this.email,
          checkbox: +this.consent
        })
      }
      this.$_navigation_handleNext(this.dataLayerObject)
    }
  }
}
</script>

<style scoped lang="scss">
.email {
  &__header-text {
    @apply mt-30 text-xl;
  }
}

.checkbox-text {
  @apply text-xs;

  a {
    @apply underline font-bold inline-block;
  }
}

:deep() .step-header__title,
:deep() .step-header__title--small {
  @apply max-w-600 mx-auto;
}

.divider {
  @apply relative text-center my-20 flex w-full justify-center;

  &__text {
    @apply inline-block bg-[#f8f8ff] z-10 px-5;
  }

  &::before {
    content: '';

    @apply block border-b border-gray-300 absolute top-3 w-full z-0;
  }
}

</style>
